"use client";

import { useState, useEffect, useCallback } from "react";
import { dmsApi } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Trash2, UserPlus } from "lucide-react";

export type ResourceShareType = "file" | "folder";

export interface ShareRecord {
  _id: string;
  resourceType: ResourceShareType;
  resourceId: string;
  sharedWithUserId: string | { _id?: string; name?: string; email?: string };
  sharedByUserId?: string | { _id?: string; name?: string; email?: string };
  permissionLevel: "view" | "edit";
  createdAt?: string;
}

function shareUserLabel(u: ShareRecord["sharedWithUserId"]): string {
  if (typeof u === "object" && u && "name" in u && u.name) {
    const email = "email" in u && u.email ? ` (${u.email})` : "";
    return `${u.name}${email}`;
  }
  return typeof u === "string" ? u : "—";
}

interface UserOption {
  _id: string;
  name: string;
  email?: string;
}

interface ShareResourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceType: ResourceShareType;
  resourceId: string;
}

function isShareOwnerOnlyError(err: unknown): boolean {
  const e = err as { status?: number; error?: string; message?: string };
  const msg = (e?.error ?? e?.message ?? "").toString().toLowerCase();
  return e?.status === 403 && (msg.includes("private folder") || msg.includes("owner"));
}

function getShareErrorMessage(err: unknown, fallback: string): string {
  if (isShareOwnerOnlyError(err)) return "Only folder owner can manage sharing.";
  const e = err as { error?: string; message?: string };
  return e?.error ?? e?.message ?? fallback;
}

export function ShareResourceDialog({
  open,
  onOpenChange,
  resourceType,
  resourceId,
}: ShareResourceDialogProps) {
  const { user: currentUser } = useAuth();
  const myId =
    (currentUser as { id?: string; _id?: string })?.id ??
    (currentUser as { _id?: string })?._id ??
    "";

  const [shares, setShares] = useState<ShareRecord[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [addUserId, setAddUserId] = useState("");
  const [addLevel, setAddLevel] = useState<"view" | "edit">("view");
  const [submitting, setSubmitting] = useState(false);

  const loadShares = useCallback(async () => {
    if (!resourceId) return;
    setLoading(true);
    try {
      const data = await dmsApi.shares.listForResource(resourceType, resourceId);
      setShares(Array.isArray(data) ? data : []);
    } catch (err: unknown) {
      toast({
        variant: "destructive",
        title: "Error",
        description: getShareErrorMessage(err, "Failed to load shares."),
      });
      setShares([]);
    } finally {
      setLoading(false);
    }
  }, [resourceType, resourceId]);

  const loadUsers = useCallback(async () => {
    try {
      const data = await dmsApi.users.list();
      const list = Array.isArray(data) ? data : [];
      setUsers(
        list.map((u: { _id: string; name?: string; email?: string }) => ({
          _id: u._id,
          name: u.name ?? "—",
          email: u.email,
        }))
      );
    } catch (err: unknown) {
      const e = err as { error?: string; message?: string };
      toast({
        variant: "destructive",
        title: "Error",
        description: e?.error ?? e?.message ?? "Failed to load users.",
      });
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadShares();
    loadUsers();
  }, [open, loadShares, loadUsers]);

  const handleAddShare = async () => {
    if (!addUserId || addUserId === myId) {
      toast({
        variant: "destructive",
        title: "Validation",
        description: "Choose a user other than yourself.",
      });
      return;
    }
    setSubmitting(true);
    try {
      await dmsApi.shares.create({
        resourceType,
        resourceId,
        sharedWithUserId: addUserId,
        permissionLevel: addLevel,
      });
      toast({ title: "Share saved", description: "Access has been updated." });
      setAddUserId("");
      setAddLevel("view");
      loadShares();
    } catch (err: unknown) {
      toast({
        variant: "destructive",
        title: "Could not share",
        description: getShareErrorMessage(err, "Request failed."),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePatchLevel = async (shareId: string, permissionLevel: "view" | "edit") => {
    try {
      await dmsApi.shares.update(shareId, { permissionLevel });
      toast({ title: "Updated", description: "Permission level saved." });
      loadShares();
    } catch (err: unknown) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description: getShareErrorMessage(err, "Request failed."),
      });
    }
  };

  const handleRevoke = async (shareId: string) => {
    try {
      await dmsApi.shares.delete(shareId);
      toast({ title: "Revoked", description: "Share removed." });
      loadShares();
    } catch (err: unknown) {
      toast({
        variant: "destructive",
        title: "Revoke failed",
        description: getShareErrorMessage(err, "Request failed."),
      });
    }
  };

  const pickableUsers = users.filter((u) => u._id !== myId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Share {resourceType === "folder" ? "folder" : "file"}</DialogTitle>
          <DialogDescription>
            Grant view or edit access to specific users. Delete is never granted via shares.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 overflow-y-auto flex-1 min-h-0">
          <div className="rounded-lg border p-3 space-y-3 bg-muted/20">
            <div className="flex items-center gap-2 text-sm font-medium">
              <UserPlus size={16} className="text-primary" />
              Add people
            </div>
            <div className="grid gap-2">
              <Label>User</Label>
              <Select value={addUserId || "none"} onValueChange={(v) => setAddUserId(v === "none" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Select —</SelectItem>
                  {pickableUsers.map((u) => (
                    <SelectItem key={u._id} value={u._id}>
                      {u.name}
                      {u.email ? ` · ${u.email}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Permission</Label>
              <Select value={addLevel} onValueChange={(v) => setAddLevel(v as "view" | "edit")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">View</SelectItem>
                  <SelectItem value="edit">Edit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={handleAddShare} disabled={submitting || !addUserId}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Grant access"}
            </Button>
          </div>

          <div>
            <Label className="text-xs uppercase text-muted-foreground">Who has access</Label>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : shares.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No shares yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="w-[120px]">Level</TableHead>
                    <TableHead className="w-[52px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shares.map((s) => {
                    return (
                      <TableRow key={s._id}>
                        <TableCell className="text-sm">{shareUserLabel(s.sharedWithUserId)}</TableCell>
                        <TableCell>
                          <Select
                            value={s.permissionLevel}
                            onValueChange={(v) => handlePatchLevel(s._id, v as "view" | "edit")}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="view">View</SelectItem>
                              <SelectItem value="edit">Edit</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            title="Revoke"
                            onClick={() => handleRevoke(s._id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
